// PROTOTYPE — right-side config panel for the selected node.
// Trigger node → pick source, and for Arbox paste an api-key + "Test connection"
// (SIMULATED here — resolves after a short delay). Action node → map each target
// field from the trigger's output fields (or a static value).
//
// All state is in-memory (owned by the parent page). No server calls.

import {useEffect, useRef, useState} from "react";
import {
    Box, Button, Chip, CircularProgress, Dialog, FormControl, IconButton,
    ListSubheader, Menu, MenuItem, Select, Switch, TextField, Typography,
} from "@mui/material";
import {
    Close as CloseIcon, CheckCircle as CheckIcon, DeleteOutline as DeleteIcon,
    BoltOutlined as BoltIcon,
    PersonOutline as UserIcon, PersonAddAlt1Outlined as UserAddIcon, PersonRemoveOutlined as UserRemoveIcon,
    ToggleOnOutlined as StatusIcon, BlockOutlined as DisabledIcon, CheckCircleOutline as EnabledIcon,
    FitnessCenterOutlined as WorkoutIcon, MonitorWeightOutlined as WeightIcon,
    AssignmentOutlined as FormIcon, ContactPageOutlined as ContactIcon,
    WebhookOutlined as WebhookIcon, DataObjectOutlined as FieldIcon,
    VisibilityOutlined as PreviewIcon,
} from "@mui/icons-material";
import {RTL} from "../../../cache/CacheRtl";
import {borders, colors} from "../../ui/colors";
import {BRAND} from "./FlowNodes";
import arboxLogo from "../../../assets/images/arbox.webp";
import fireberryLogo from "../../../assets/images/fireberry.webp";
import {ReactComponent as ProgressLogo} from "../../../assets/images/logo.svg";
import RichTextField from "./RichTextField";
import HeadersEditor from "../HeadersEditor";
import JsonBlock from "./JsonBlock";
import {SYSTEMS, triggerByKind, triggersForSystem, availableSystemList, FORMULA_FUNCTIONS, FORMULA_OPERATORS, FILTER_OPERATORS, MATCH_FIELD_CHOICES} from "./nodeCatalog";
import {availableTriggerKinds, availableSystems} from "./catalogStore";
import {connectArbox} from "../../../services/IntegrationService";
import {loadPickerOptions} from "./pickerData";

// A name-dropdown for a picked id (program template / nutrition menu / form template). Lists the
// company's items by NAME; the chosen id is stored (as a STATIC binding) by the caller. Reuses the
// existing admin list services via loadPickerOptions.
function IdPicker({picker, companyId, value, onChange}) {
    const [options, setOptions] = useState(null); // null = loading
    useEffect(() => {
        let alive = true;
        setOptions(null);
        loadPickerOptions(picker, companyId).then(opts => { if (alive) setOptions(opts); });
        return () => { alive = false; };
    }, [picker, companyId]);

    if (options === null) {
        return <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <CircularProgress size={14}/><Typography sx={{fontSize: "12px", color: colors.textMuted}}>טוען…</Typography>
        </Box>;
    }
    return (
        <FormControl fullWidth size="small">
            <Select
                value={value != null && value !== "" ? String(value) : ""}
                displayEmpty
                onChange={e => onChange(e.target.value)}
                sx={{fontSize: "12px"}}
                renderValue={v => {
                    if (!v) return <span style={{color: colors.textMuted}}>בחר…</span>;
                    const hit = options.find(o => String(o.id) === String(v));
                    return hit ? hit.name : `#${v}`;
                }}
            >
                <MenuItem value=""><em>ללא</em></MenuItem>
                {options.length === 0 && <MenuItem value="" disabled><em>אין פריטים בחברה</em></MenuItem>}
                {options.map(o => (
                    <MenuItem key={o.id} value={String(o.id)} sx={{fontSize: "12px"}}>{o.name}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

const labelSx = {fontSize: "12px", fontWeight: 600, color: colors.textSecondary, mb: 0.5};

const triggerKey = (t) => t.kind === "INTERNAL_EVENT" ? `INTERNAL_EVENT:${t.eventType}` : t.kind;

// Per-trigger icon (by kind, or by internal eventType).
const TRIGGER_ICON = {
    ARBOX_NEW_CLIENT: UserAddIcon, ARBOX_CLIENT_STATUS: StatusIcon,
    ARBOX_CLIENT_DISABLED: DisabledIcon, ARBOX_CLIENT_ENABLED: EnabledIcon,
    FIREBERRY_NEW_CONTACT: ContactIcon, FIREBERRY_CONTACT_UPDATED: ContactIcon,
    WEBHOOK_RECEIVED: WebhookIcon,
    USER_CREATED: UserAddIcon, USER_UPDATED: UserIcon, USER_DELETED: UserRemoveIcon,
    WORKOUT_COMPLETED: WorkoutIcon, WEIGHT_UPDATED: WeightIcon, FORM_COMPLETED: FormIcon,
};
const triggerIconFor = (t) => TRIGGER_ICON[t.eventType] ?? TRIGGER_ICON[t.kind] ?? BoltIcon;

// System logo/icon for the picker.
const SystemIcon = ({iconType, size = 28}) => {
    if (iconType === "arbox") return <Box component="img" src={arboxLogo} alt="Arbox" sx={{width: size, height: size, borderRadius: "6px"}}/>;
    if (iconType === "fireberry") return <Box component="img" src={fireberryLogo} alt="Fireberry" sx={{width: size, height: size, borderRadius: "50%"}}/>;
    if (iconType === "webhook") return (
        <Box sx={{width: size, height: size, borderRadius: "6px", bgcolor: colors.textSecondary,
                  display: "flex", alignItems: "center", justifyContent: "center"}}>
            <WebhookIcon sx={{fontSize: size * 0.68, color: "#fff"}}/>
        </Box>
    );
    // Progress — the brand "R" logo on a teal app-icon tile (the SVG is light-on-dark).
    return (
        <Box sx={{width: size, height: size, borderRadius: "6px", bgcolor: BRAND,
                  display: "flex", alignItems: "center", justifyContent: "center"}}>
            <ProgressLogo style={{width: size * 0.5, height: size * 0.62}}/>
        </Box>
    );
};

function TriggerConfig({node, companyId, onChange}) {
    const current = node.data.trigger;
    // Which system's triggers to show. Default to the current trigger's system.
    const [systemId, setSystemId] = useState(current?.system || null);
    const [apiKey, setApiKey] = useState("");   // never restored from saved data — the key lives encrypted server-side
    const [testState, setTestState] = useState(node.data.keyOk ? "ok" : "idle"); // idle | testing | ok | fail
    const [testError, setTestError] = useState("");
    const [replacing, setReplacing] = useState(false); // showing the key input again to swap a connected key

    // Only show systems/triggers the server actually registered (falls back to the full
    // static catalog until /bricks has loaded). Arbox/Fireberry appear here automatically
    // once their trigger bricks exist server-side.
    const systemsList = availableSystemList(availableSystems());
    const system = SYSTEMS.find(s => s.id === systemId) || null;
    const triggers = systemId ? triggersForSystem(systemId, availableTriggerKinds()) : [];

    const pickTrigger = (composite) => {
        const [kind, eventType] = composite.split(":");
        const t = triggerByKind(kind, eventType);
        setTestState("idle");
        setTestError("");
        onChange({...node.data, trigger: t, keyOk: false});
    };

    // REAL connect: validate the key against the live Arbox API, discover THIS company's fields, and
    // persist the encrypted key server-side. We keep only the integrationId + discovered fields on the
    // node (never the raw key). The discovered fields drive the {{arbox.*}} tokens the builder offers.
    const testKey = async () => {
        setTestState("testing");
        setTestError("");
        try {
            const res = await connectArbox(apiKey.trim(), companyId);
            const {integrationId, last4, fields} = res.data || {};
            setTestState("ok");
            setApiKey("");       // don't keep the raw key in memory once it's stored
            setReplacing(false); // back to the "connected" view (masked key + replace button)
            onChange({
                ...node.data,
                keyOk: true,
                integrationId,
                integrationLast4: last4,
                discoveredFields: fields || [],
            });
        } catch (e) {
            setTestState("fail");
            setTestError(e?.response?.data?.errorMessage || "החיבור נכשל — בדוק את המפתח");
        }
    };

    // The trigger's fields for the mapping UI: the company's discovered Arbox fields once connected,
    // else the static catalog fallback (so the chips show something before connecting).
    const triggerFields = (node.data.discoveredFields && node.data.discoveredFields.length)
        ? node.data.discoveredFields
        : (current?.outputs || []);

    return (
        <>
            {/* Step 1 — choose the system */}
            <Box>
                <Typography sx={labelSx}>מערכת</Typography>
                <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                    {systemsList.map(s => {
                        const active = s.id === systemId;
                        return (
                            <Box
                                key={s.id}
                                onClick={() => setSystemId(s.id)}
                                sx={{
                                    display: "flex", alignItems: "center", gap: 1.25,
                                    p: 1, borderRadius: borders.radius.md, cursor: "pointer",
                                    border: `1.5px solid ${active ? BRAND : colors.border}`,
                                    bgcolor: active ? `${BRAND}0D` : colors.white,
                                    transition: "all 0.12s",
                                    "&:hover": {borderColor: BRAND},
                                }}
                            >
                                <SystemIcon iconType={s.iconType}/>
                                <Box sx={{flex: 1}}>
                                    <Typography sx={{fontSize: "13px", fontWeight: 600, color: colors.textPrimary}}>{s.label}</Typography>
                                    <Typography sx={{fontSize: "11px", color: colors.textMuted}}>{s.description}</Typography>
                                </Box>
                                {active && <CheckIcon sx={{fontSize: 18, color: BRAND}}/>}
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            {/* Step 2 — choose a trigger within the system */}
            {system && (
                <Box>
                    <Typography sx={labelSx}>טריגר ב-{system.label}</Typography>
                    <FormControl fullWidth size="small">
                        <Select
                            value={current && current.system === systemId ? triggerKey(current) : ""}
                            displayEmpty
                            onChange={e => pickTrigger(e.target.value)}
                            renderValue={v => v ? current?.label : "בחר טריגר…"}
                        >
                            {triggers.map(t => {
                                const TIcon = triggerIconFor(t);
                                return (
                                    <MenuItem key={triggerKey(t)} value={triggerKey(t)}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                            <TIcon sx={{fontSize: 18, color: BRAND, flexShrink: 0}}/>
                                            <Box>
                                                <Typography sx={{fontSize: "13px"}}>{t.label}</Typography>
                                                {/* Human description only for external systems; internal
                                                    events' descriptions are dev event names, hidden from the user. */}
                                                {t.system !== "PROGRESS" && (
                                                    <Typography sx={{fontSize: "11px", color: colors.textMuted}}>{t.description}</Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                </Box>
            )}

            {systemId === "WEBHOOK" && current?.system === "WEBHOOK" && (
                <Box>
                    <Typography sx={labelSx}>כתובת ה-Webhook</Typography>
                    <TextField
                        fullWidth size="small" InputProps={{readOnly: true}}
                        value="https://api.progress-workout.com/webhook/flows/ab12cd34"
                        sx={{"& input": {fontFamily: "monospace", fontSize: "11px", color: colors.textSecondary}}}
                    />
                    <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.5}}>
                        שלח בקשת POST לכתובת זו כדי להפעיל את הזרימה. (אב-טיפוס: כתובת לדוגמה.)
                    </Typography>
                </Box>
            )}

            {system?.needsApiKey && current?.system === systemId && (
                <Box>
                    <Typography sx={labelSx}>מפתח API של {system.label}</Typography>
                    {/* Once connected the key is STORED (encrypted, server-side) — so we drop the input
                        entirely and show a confirmation + a "replace key" action. The raw key is never
                        held in the builder; the flow only references the integration by id. */}
                    {node.data.keyOk && !replacing ? (
                        <Box sx={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                            p: 1, border: borders.default, borderRadius: borders.radius.md, bgcolor: colors.bg,
                        }}>
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                <CheckIcon sx={{fontSize: 18, color: colors.success}}/>
                                <Typography sx={{fontSize: "12px", color: colors.textPrimary}}>
                                    {node.data.integrationLast4
                                        ? `המפתח נשמר · מסתיים ב-${node.data.integrationLast4}`
                                        : "המפתח נשמר"}
                                </Typography>
                            </Box>
                            <Button
                                variant="text" size="small"
                                onClick={() => {setReplacing(true); setTestState("idle"); setApiKey("");}}
                                sx={{whiteSpace: "nowrap", minWidth: 72}}
                            >
                                החלף מפתח
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{display: "flex", gap: 1}}>
                                <TextField
                                    fullWidth size="small" type="password"
                                    placeholder="הדבק את מפתח ה-API"
                                    value={apiKey}
                                    onChange={e => {setApiKey(e.target.value); setTestState("idle");}}
                                    sx={{"& input": {fontFamily: "monospace", fontSize: "12px"}}}
                                />
                                <Button
                                    variant="outlined" size="small"
                                    onClick={testKey}
                                    disabled={testState === "testing" || !apiKey.trim()}
                                    sx={{whiteSpace: "nowrap", minWidth: 92}}
                                >
                                    {testState === "testing" ? <CircularProgress size={16}/> : "בדוק חיבור"}
                                </Button>
                            </Box>
                            {/* Let the user back out of "replace" without losing the existing connection. */}
                            {replacing && (
                                <Button variant="text" size="small" onClick={() => {setReplacing(false); setTestState("ok");}}
                                        sx={{mt: 0.5, minWidth: 0}}>
                                    ביטול
                                </Button>
                            )}
                            {testState === "fail" && (
                                <Typography sx={{fontSize: "12px", color: colors.error, mt: 0.75}}>
                                    {testError || "החיבור נכשל — בדוק את המפתח"}
                                </Typography>
                            )}
                        </>
                    )}
                </Box>
            )}

            {current && current.system === systemId && (
                <Box>
                    <Typography sx={labelSx}>שדות שהטריגר מספק</Typography>
                    <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                        {triggerFields.map(f => (
                            <Chip key={f.path} label={f.label} size="small"
                                  sx={{fontSize: "11px", bgcolor: colors.primarySubtle, color: colors.textSecondary}}/>
                        ))}
                    </Box>
                    {system?.needsApiKey && !node.data.keyOk && (
                        <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.5}}>
                            חבר את {system.label} כדי לטעון את השדות של החברה שלך.
                        </Typography>
                    )}
                </Box>
            )}
        </>
    );
}

// Excel-like formula editor for the data brick: name the output + write a free
// formula referencing {{upstream.fields}} and calling functions. An insert menu
// drops field tokens; a cheat-sheet lists functions.
function FormulaConfig({node, upstreamGroups, onChange}) {
    const formula = node.data.formula || {outputName: "", expression: ""};
    const taRef = useRef(null);
    const [fieldMenu, setFieldMenu] = useState(null);

    const set = (patch) => onChange({...node.data, formula: {...formula, ...patch}});

    const insertAtCaret = (text) => {
        const ta = taRef.current;
        const expr = formula.expression || "";
        if (!ta) { set({expression: expr + text}); return; }
        const s = ta.selectionStart ?? expr.length;
        const e = ta.selectionEnd ?? expr.length;
        set({expression: expr.slice(0, s) + text + expr.slice(e)});
    };

    const outName = formula.outputName?.trim() || "result";
    const prefix = `data_${node.id}`;
    const hasUpstream = upstreamGroups.some(g => g.fields.length);

    return (
        <>
            <Box>
                <Typography sx={{fontSize: "14px", fontWeight: 700, color: colors.textPrimary}}>עיבוד נתונים</Typography>
                <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.25}}>
                    כתוב נוסחה חופשית (כמו אקסל), השתמש ב-{"{{שדות}}"} וקרא לפונקציות
                </Typography>
            </Box>

            {/* Output field name — used as a {{data_n.name}} token, so no spaces (auto-replaced with _). */}
            <Box>
                <Typography sx={labelSx}>שם שדה התוצאה</Typography>
                <TextField fullWidth size="small" placeholder="לדוגמה: fullName"
                           value={formula.outputName || ""}
                           onChange={e => set({outputName: e.target.value.replace(/\s+/g, "_")})}
                           sx={{"& input": {fontFamily: "monospace", fontSize: "12px"}}}/>
                <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.5}}>
                    ללא רווחים — שמש לשם השדה בשלבים הבאים (למשל {"{{...}}"}).
                </Typography>
            </Box>

            {/* Formula editor */}
            <Box>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5}}>
                    <Typography sx={labelSx}>נוסחה</Typography>
                    <Button size="small" startIcon={<FieldIcon sx={{fontSize: 15}}/>} disabled={!hasUpstream}
                            onMouseDown={e => {e.preventDefault(); setFieldMenu(e.currentTarget);}}
                            sx={{fontSize: "11px", color: BRAND}}>
                        הוסף שדה
                    </Button>
                </Box>
                <TextField
                    inputRef={taRef}
                    fullWidth multiline minRows={3}
                    placeholder={'{{arbox.first_name}} + " " + {{arbox.last_name}}'}
                    value={formula.expression || ""}
                    onChange={e => set({expression: e.target.value})}
                    sx={{"& textarea": {fontFamily: "monospace", fontSize: "12px", direction: "ltr", textAlign: "left"}}}
                />
                {/* Operators — insert directly, spreadsheet-style */}
                <Box sx={{display: "flex", gap: 0.5, mt: 0.75}}>
                    {FORMULA_OPERATORS.map(o => (
                        <Chip key={o.op} label={o.op} size="small" title={o.help}
                              onClick={() => insertAtCaret(` ${o.op} `)}
                              sx={{fontSize: "13px", fontFamily: "monospace", fontWeight: 700, cursor: "pointer",
                                   minWidth: 34, bgcolor: colors.bg, "&:hover": {bgcolor: `${BRAND}22`}}}/>
                    ))}
                    <Chip label={'" "'} size="small" title="טקסט קבוע"
                          onClick={() => insertAtCaret(' " " ')}
                          sx={{fontSize: "11px", fontFamily: "monospace", cursor: "pointer",
                               bgcolor: colors.bg, "&:hover": {bgcolor: `${BRAND}22`}}}/>
                </Box>
                <Typography sx={{fontSize: "10px", color: colors.textMuted, mt: 0.75}}>
                    {'דוגמאות:  {{a}} + " " + {{b}}   ·   "+972" + {{arbox.phone}}   ·   {{x}} + {{y}}'}
                </Typography>
            </Box>

            {/* Function cheat-sheet — only for what operators can't do */}
            <Box>
                <Typography sx={labelSx}>פונקציות</Typography>
                <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                    {FORMULA_FUNCTIONS.map(f => (
                        <Chip key={f.name} label={f.name} size="small" onClick={() => insertAtCaret(f.sig)}
                              title={`${f.help} — ${f.sig}`}
                              sx={{fontSize: "11px", fontFamily: "monospace", cursor: "pointer",
                                   bgcolor: colors.primarySubtle, color: colors.textSecondary,
                                   "&:hover": {bgcolor: `${BRAND}22`}}}/>
                    ))}
                </Box>
                <Typography sx={{fontSize: "10px", color: colors.textMuted, mt: 0.5}}>
                    לחיצה מוסיפה לנוסחה
                </Typography>
            </Box>

            {/* Output preview */}
            <Box>
                <Typography sx={labelSx}>הפלט של השלב</Typography>
                <Box sx={{p: 1, border: `1px dashed ${BRAND}`, borderRadius: borders.radius.md, bgcolor: `${BRAND}0D`}}>
                    <Typography sx={{fontSize: "12px", fontFamily: "monospace", color: BRAND, direction: "ltr", textAlign: "left"}}>
                        {`{{${prefix}.${outName}}}`}
                    </Typography>
                    <Typography sx={{fontSize: "10px", color: colors.textMuted}}>זמין לשלבים הבאים</Typography>
                </Box>
            </Box>

            <Menu anchorEl={fieldMenu} open={Boolean(fieldMenu)} onClose={() => setFieldMenu(null)}>
                {upstreamGroups.flatMap(g => [
                    <ListSubheader key={`h-${g.sourceId}`} sx={{fontSize: "11px", fontWeight: 700, color: BRAND, lineHeight: "28px"}}>
                        {g.sourceLabel}
                    </ListSubheader>,
                    ...g.fields.map(f => (
                        <MenuItem key={f.path} sx={{pr: 3}}
                                  onClick={() => {setFieldMenu(null); insertAtCaret(`{{${f.path}}}`);}}>
                            <Box>
                                <Typography sx={{fontSize: "12px", fontFamily: "monospace"}}>{`{{${f.path}}}`}</Typography>
                                <Typography sx={{fontSize: "10px", color: colors.textMuted}}>{f.label}</Typography>
                            </Box>
                        </MenuItem>
                    )),
                ])}
            </Menu>
        </>
    );
}

// Filter / condition brick: continue only if the condition holds, else stop the
// branch. Structured builder (upstream field + operator + value) with an
// advanced free-text expression escape hatch.
function FilterConfig({node, upstreamGroups, onChange}) {
    const cond = node.data.condition || {mode: "STRUCTURED", field: "", operator: "EQ", value: ""};
    const set = (patch) => onChange({...node.data, condition: {...cond, ...patch}});
    const op = FILTER_OPERATORS.find(o => o.value === cond.operator);
    const isUnary = Boolean(op?.unary);
    const hasUpstream = upstreamGroups.some(g => g.fields.length);
    const allFields = upstreamGroups.flatMap(g => g.fields.map(f => ({...f, source: g.sourceLabel})));

    return (
        <>
            <Box>
                <Typography sx={{fontSize: "14px", fontWeight: 700, color: colors.textPrimary}}>תנאי / סינון</Typography>
                <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.25}}>
                    הזרימה תמשיך רק אם התנאי מתקיים. אחרת — הענף ייעצר.
                </Typography>
            </Box>

            {/* mode toggle */}
            <FormControl size="small">
                <Select value={cond.mode || "STRUCTURED"} onChange={e => set({mode: e.target.value})} sx={{fontSize: "12px"}}>
                    <MenuItem value="STRUCTURED">תנאי פשוט</MenuItem>
                    <MenuItem value="ADVANCED">ביטוי מתקדם</MenuItem>
                </Select>
            </FormControl>

            {cond.mode === "ADVANCED" ? (
                <Box>
                    <Typography sx={labelSx}>ביטוי (מתקיים אם התוצאה אמת)</Typography>
                    <TextField
                        fullWidth multiline minRows={2}
                        placeholder={'{{arbox.user_role}} == "member"'}
                        value={cond.expression || ""}
                        onChange={e => set({expression: e.target.value})}
                        sx={{"& textarea": {fontFamily: "monospace", fontSize: "12px", direction: "ltr", textAlign: "left"}}}
                    />
                    <Typography sx={{fontSize: "10px", color: colors.textMuted, mt: 0.5}}>
                        {'אופרטורים: == != > < , וגם contains(). דוגמה: {{arbox.active}} == 1'}
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* field */}
                    <Box>
                        <Typography sx={labelSx}>שדה</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={cond.field || ""} displayEmpty
                                    onChange={e => set({field: e.target.value})}
                                    renderValue={v => v ? `{{${v}}}` : "בחר שדה מהזרימה…"} sx={{fontSize: "12px"}}>
                                {!hasUpstream && <MenuItem value="" disabled><em>אין שדות זמינים</em></MenuItem>}
                                {allFields.map(f => (
                                    <MenuItem key={f.path} value={f.path}>
                                        <Box>
                                            <Typography sx={{fontSize: "12px", fontFamily: "monospace"}}>{`{{${f.path}}}`}</Typography>
                                            <Typography sx={{fontSize: "10px", color: colors.textMuted}}>{f.label} · {f.source}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    {/* operator */}
                    <Box>
                        <Typography sx={labelSx}>תנאי</Typography>
                        <FormControl fullWidth size="small">
                            <Select value={cond.operator || "EQ"} onChange={e => set({operator: e.target.value})} sx={{fontSize: "12px"}}>
                                {FILTER_OPERATORS.map(o => <MenuItem key={o.value} value={o.value} sx={{fontSize: "12px"}}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                    {/* value (hidden for unary operators) */}
                    {!isUnary && (
                        <Box>
                            <Typography sx={labelSx}>ערך</Typography>
                            <TextField fullWidth size="small" placeholder="לדוגמה: member"
                                       value={cond.value || ""} onChange={e => set({value: e.target.value})}
                                       sx={{"& input": {fontSize: "12px"}}}/>
                        </Box>
                    )}
                    {/* human-readable preview */}
                    {cond.field && (
                        <Box sx={{p: 1, border: `1px dashed ${BRAND}`, borderRadius: borders.radius.md, bgcolor: `${BRAND}0D`}}>
                            <Typography sx={{fontSize: "11px", color: colors.textSecondary}}>
                                המשך אם: <b>{`{{${cond.field}}}`}</b> {op?.label}{!isUnary && cond.value ? <> "<b>{cond.value}</b>"</> : ""}
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </>
    );
}

// HTTP brick config: URL + method + a large free-form body textarea (insert {{fields}}), a preview
// of what will actually be sent, and key/value headers (reusing the shared HeadersEditor).
function HttpConfig({node, action, upstreamGroups, onChange}) {
    const bodyRef = useRef(null);
    const [fieldMenu, setFieldMenu] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    const mapping = node.data.mapping || {};
    const options = node.data.options || {};
    const headers = node.data.headers || {};

    const mapVal = (key) => (mapping[key]?.value ?? "");
    // URL + body are STATIC templates (free-form, with {{...}} interpolation).
    const setMapVal = (key, value) => onChange({...node.data, mapping: {...mapping, [key]: {type: "STATIC", value}}});
    const setOption = (key, value) => onChange({...node.data, options: {...options, [key]: value}});
    const setHeaders = (next) => onChange({...node.data, headers: next});

    const method = options.method ?? "POST";
    const methodChoices = (action.options?.find(o => o.key === "method")?.choices) || [];
    const hasUpstream = upstreamGroups.some(g => g.fields.length);

    // Insert a {{path}} token into the body at the caret.
    const insertField = (path) => {
        setFieldMenu(null);
        const ta = bodyRef.current;
        const cur = mapVal("body");
        if (!ta) { setMapVal("body", cur + `{{${path}}}`); return; }
        const s = ta.selectionStart ?? cur.length;
        const e = ta.selectionEnd ?? cur.length;
        const next = cur.slice(0, s) + `{{${path}}}` + cur.slice(e);
        setMapVal("body", next);
        requestAnimationFrame(() => {
            ta.focus();
            const pos = s + `{{${path}}}`.length;
            ta.setSelectionRange(pos, pos);
        });
    };

    // Substitute {{path}} with each field's sample value, for the "what will be sent" preview.
    const sampleFor = (path) => {
        for (const g of upstreamGroups) {
            const f = g.fields.find(x => x.path === path);
            if (f && f.sample !== undefined) return String(f.sample);
        }
        return `{{${path}}}`;
    };
    const previewUrl = () => (mapVal("url") || "").replace(/\{\{\s*([\w.]+)\s*}}/g, (_m, p) => sampleFor(p));

    // Body mode: CUSTOM = the free-form template; ALL = auto-send every available field.
    const bodyMode = options.bodyMode ?? "CUSTOM";
    const setBodyMode = (v) => setOption("bodyMode", v);

    // Pretty-print a JSON string with 2-space indentation; leave it as-is if it isn't valid JSON.
    const prettyJson = (text) => {
        try {
            return JSON.stringify(JSON.parse(text), null, 2);
        } catch {
            return text;
        }
    };

    // Preview: in CUSTOM, substitute samples into the template; in ALL, show a sample of every
    // available field (trigger + all upstream outputs). Both are prettified JSON.
    const previewBody = () => {
        if (bodyMode === "ALL") {
            const obj = {};
            upstreamGroups.forEach(g => g.fields.forEach(f => {
                obj[f.path] = f.sample !== undefined ? f.sample : `{{${f.path}}}`;
            }));
            return JSON.stringify(obj, null, 2);
        }
        const substituted = (mapVal("body") || "").replace(/\{\{\s*([\w.]+)\s*}}/g, (_m, p) => sampleFor(p));
        return prettyJson(substituted);
    };

    // The full preview text for the code block: any headers, then the (prettified) body.
    const buildPreviewText = () => {
        const headerLines = Object.entries(headers).map(([k, v]) => `${k}: ${v}`);
        const body = previewBody() || "(empty body)";
        return headerLines.length ? `${headerLines.join("\n")}\n\n${body}` : body;
    };

    return (
        <>
            <Box>
                <Typography sx={{fontSize: "14px", fontWeight: 700, color: colors.textPrimary}}>{action.label}</Typography>
                <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.25}}>{action.description}</Typography>
            </Box>

            {/* Method + URL */}
            <Box sx={{display: "flex", gap: 1}}>
                <FormControl size="small" sx={{width: 110, flexShrink: 0}}>
                    <Select value={method} onChange={e => setOption("method", e.target.value)} sx={{fontSize: "12px"}}>
                        {methodChoices.map(c => <MenuItem key={c.value} value={c.value} sx={{fontSize: "12px"}}>{c.label}</MenuItem>)}
                    </Select>
                </FormControl>
                <TextField fullWidth size="small" placeholder="https://example.com/hook"
                           value={mapVal("url")} onChange={e => setMapVal("url", e.target.value)}
                           sx={{"& input": {fontSize: "12px", fontFamily: "monospace", direction: "ltr"}}}/>
            </Box>

            {/* Body — mode toggle (Custom / All fields), then the editor (only in Custom) */}
            <Box>
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75}}>
                    <Typography sx={labelSx}>גוף הבקשה</Typography>
                    <Button size="small" startIcon={<PreviewIcon sx={{fontSize: 16}}/>}
                            onClick={() => setPreviewOpen(true)} sx={{fontSize: "11px", color: colors.textSecondary, minWidth: 0}}>
                        תצוגה מקדימה
                    </Button>
                </Box>

                {/* Two-way segmented toggle */}
                <Box sx={{display: "flex", gap: 0.75, mb: 1}}>
                    {[{v: "CUSTOM", label: "מותאם אישית"}, {v: "ALL", label: "כל השדות"}].map(opt => {
                        const active = bodyMode === opt.v;
                        return (
                            <Box key={opt.v} onClick={() => setBodyMode(opt.v)} sx={{
                                flex: 1, textAlign: "center", cursor: "pointer", py: 0.75, borderRadius: borders.radius.sm,
                                fontSize: "12px", fontWeight: 600,
                                border: `1.5px solid ${active ? BRAND : colors.border}`,
                                bgcolor: active ? `${BRAND}0D` : colors.white,
                                color: active ? BRAND : colors.textSecondary,
                                transition: "all 0.12s", "&:hover": {borderColor: BRAND},
                            }}>
                                {opt.label}
                            </Box>
                        );
                    })}
                </Box>

                {bodyMode === "ALL" ? (
                    <Box sx={{p: 1.25, border: borders.default, borderRadius: borders.radius.md, bgcolor: colors.bg}}>
                        <Typography sx={{fontSize: "12px", color: colors.textPrimary, fontWeight: 600}}>
                            יישלחו כל השדות הזמינים בשלב זה
                        </Typography>
                        <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.25}}>
                            נתוני הטריגר וכל הפלטים מהשלבים הקודמים — כאובייקט JSON. לחץ על "תצוגה מקדימה" לראות.
                        </Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{display: "flex", justifyContent: "flex-end", mb: 0.5}}>
                            <Button size="small" startIcon={<FieldIcon sx={{fontSize: 16}}/>} disabled={!hasUpstream}
                                    onClick={e => setFieldMenu(e.currentTarget)} sx={{fontSize: "11px", color: BRAND, minWidth: 0}}>
                                הוסף שדה
                            </Button>
                        </Box>
                        <TextField
                            inputRef={bodyRef}
                            fullWidth multiline minRows={6} maxRows={16}
                            placeholder={'{\n  "name": "{{trainee.name}}",\n  "email": "{{trainee.email}}"\n}'}
                            value={mapVal("body")} onChange={e => setMapVal("body", e.target.value)}
                            sx={{"& textarea": {fontSize: "12px", fontFamily: "monospace", direction: "ltr", lineHeight: 1.5}}}
                        />
                        <Typography sx={{fontSize: "10px", color: colors.textMuted, mt: 0.5}}>
                            {"שלב נתונים עם {{שדה}} — למשל {{trainee.email}}"}
                        </Typography>
                    </>
                )}
            </Box>

            {/* Headers */}
            <Box>
                <HeadersEditor value={headers} onChange={setHeaders}/>
            </Box>

            {/* Insert-field menu (shared shape with the other editors) */}
            <Menu anchorEl={fieldMenu} open={Boolean(fieldMenu)} onClose={() => setFieldMenu(null)}>
                {upstreamGroups.flatMap(g => [
                    <ListSubheader key={`h-${g.sourceId}`} sx={{fontSize: "11px", fontWeight: 700, color: `${BRAND} !important`, lineHeight: 2}}>
                        {g.sourceLabel}
                    </ListSubheader>,
                    ...g.fields.map(f => (
                        <MenuItem key={f.path} onClick={() => insertField(f.path)} sx={{pr: 3}}>
                            <Box>
                                <Typography sx={{fontSize: "12px", fontFamily: "monospace"}}>{`{{${f.path}}}`}</Typography>
                                <Typography sx={{fontSize: "10px", color: colors.textMuted}}>{f.label}</Typography>
                            </Box>
                        </MenuItem>
                    )),
                ])}
            </Menu>

            {/* Preview dialog — what will actually be sent (sample values substituted).
                RTL must be INSIDE the Dialog: it renders in a Portal, escaping the panel's <RTL>
                wrapper, so without this the Emotion RTL cache (text-align flipping etc.) is inactive. */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)}
                    PaperProps={{sx: {borderRadius: borders.radius.md, maxWidth: 600}}} fullWidth>
                <RTL>
                    <Box dir="rtl">
                        {/* Header: title first → RIGHT, X last → LEFT. */}
                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between",
                                  px: 2, py: 1.25, borderBottom: borders.default, bgcolor: colors.bg}}>
                            <Typography sx={{fontSize: "14px", fontWeight: 700}}>מה יישלח</Typography>
                            <IconButton size="small" onClick={() => setPreviewOpen(false)}><CloseIcon fontSize="small"/></IconButton>
                        </Box>
                        <Box sx={{p: 2}}>
                            <Typography sx={{fontSize: "11px", color: colors.textMuted, mb: 0.75}}>
                                עם ערכים לדוגמה מהזרימה
                            </Typography>
                            <JsonBlock
                                header={`${method} ${previewUrl() || "(no url)"}`}
                                json={buildPreviewText()}
                            />
                        </Box>
                    </Box>
                </RTL>
            </Dialog>
        </>
    );
}

function ActionConfig({node, upstreamGroups, companyId, onChange}) {
    // Works for both action nodes (data.action) and data-transform nodes
    // (data.brick) — both expose targets/options/outputs. Data nodes have a
    // fixed kind (chosen at drop), so their type dropdown is hidden.
    const isDataNode = node.type === "data";
    const action = node.data.action || node.data.brick;

    // Formula-based data brick → the Excel-like editor.
    if (action?.formula) {
        return <FormulaConfig node={node} upstreamGroups={upstreamGroups} onChange={onChange}/>;
    }
    // Filter/condition brick → the condition builder.
    if (action?.filter) {
        return <FilterConfig node={node} upstreamGroups={upstreamGroups} onChange={onChange}/>;
    }
    // HTTP brick → dedicated config (URL + method + large body textarea + preview + headers).
    if (action?.kind === "HTTP") {
        return <HttpConfig node={node} action={action} upstreamGroups={upstreamGroups} onChange={onChange}/>;
    }
    const mapping = node.data.mapping || {};

    // Each input maps to {type: "FIELD"|"STATIC", value}. FIELD → an upstream
    // output path; STATIC → a literal typed by the user (e.g. a fixed recipient).
    const setMap = (inputKey, next) => {
        onChange({...node.data, mapping: {...mapping, [inputKey]: next}});
    };
    const cur = (k) => mapping[k] || {type: "FIELD", value: ""};

    // Options = behavior settings (booleans/selects), separate from data mapping.
    const options = node.data.options || {};
    const optionVal = (o) => (options[o.key] !== undefined ? options[o.key] : o.default);
    const setOption = (key, value) => onChange({...node.data, options: {...options, [key]: value}});

    const hasUpstream = upstreamGroups.some(g => g.fields.length);

    return (
        <>
            {/* A node's kind is fixed at drop time — to change it, delete the node
                and drag a new one. So we show the kind as a static header, not a
                dropdown (removes the confusing in-place-change path). */}
            <Box>
                <Typography sx={{fontSize: "14px", fontWeight: 700, color: colors.textPrimary}}>{action?.label}</Typography>
                <Typography sx={{fontSize: "11px", color: colors.textMuted, mt: 0.25}}>{action?.description}</Typography>
            </Box>

            {action && (
                <Box>
                    <Typography sx={labelSx}>{isDataNode ? "קלט" : "מיפוי שדות"}</Typography>
                    <Typography sx={{fontSize: "11px", color: colors.textMuted, mb: 1}}>
                        לכל שדה: בחר ערך משלב קודם, או הזן ערך קבוע
                    </Typography>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                        {action.targets.map(t => {
                            const m = cur(t.key);

                            // Rich-text inputs (e.g. email body) get the template editor
                            // with an inline insert-{{field}} menu — no FIELD/STATIC toggle.
                            if (t.richText) {
                                return (
                                    <Box key={t.key}>
                                        <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textPrimary, mb: 0.5}}>
                                            {t.label}{t.required && <span style={{color: colors.error}}> *</span>}
                                        </Typography>
                                        <RichTextField
                                            value={m.type === "HTML" ? m.value : ""}
                                            onChange={html => setMap(t.key, {type: "HTML", value: html})}
                                            upstreamGroups={upstreamGroups}
                                        />
                                    </Box>
                                );
                            }

                            // "Find the trainee" block (matchMode): a by-email/by-id selector (writes the
                            // matchField OPTION) + the value below, which can be dynamic (משדה, e.g.
                            // {{arbox.email}}) or a fixed value (ערך קבוע). Rendered first & required — it's
                            // the primary decision (which trainee), before any field edits.
                            if (t.matchMode) {
                                const matchField = optionVal({key: "matchField", default: "EMAIL"});
                                const valMode = m.type === "STATIC" ? "STATIC" : "FIELD";
                                return (
                                    <Box key={t.key} sx={{
                                        p: 1, border: `1px solid ${colors.primary}`, borderRadius: borders.radius.md, bgcolor: colors.bg,
                                    }}>
                                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75}}>
                                            <Typography sx={{fontSize: "12px", fontWeight: 700, color: colors.textPrimary}}>
                                                {t.label}<span style={{color: colors.error}}> *</span>
                                            </Typography>
                                            <FormControl size="small">
                                                <Select
                                                    value={matchField}
                                                    onChange={e => setOption("matchField", e.target.value)}
                                                    sx={{fontSize: "11px", height: 26}}
                                                >
                                                    {MATCH_FIELD_CHOICES.map(c => (
                                                        <MenuItem key={c.value} value={c.value} sx={{fontSize: "11px"}}>{c.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        {/* value source: dynamic (from a previous node) or a fixed value */}
                                        <Box sx={{display: "flex", justifyContent: "flex-end", mb: 0.5}}>
                                            <FormControl size="small">
                                                <Select
                                                    value={valMode}
                                                    onChange={e => setMap(t.key, {type: e.target.value, value: ""})}
                                                    sx={{fontSize: "11px", height: 24}}
                                                >
                                                    <MenuItem value="FIELD">משדה</MenuItem>
                                                    <MenuItem value="STATIC">ערך קבוע</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        {valMode === "STATIC" ? (
                                            <TextField
                                                fullWidth size="small"
                                                placeholder={matchField === "EMAIL" ? "אימייל, למשל dana@example.com" : "מזהה מתאמן, למשל 8123"}
                                                value={m.value}
                                                onChange={e => setMap(t.key, {type: "STATIC", value: e.target.value})}
                                                sx={{"& input": {fontSize: "12px"}}}
                                            />
                                        ) : (
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={m.value || ""}
                                                    displayEmpty
                                                    onChange={e => setMap(t.key, {type: "FIELD", value: e.target.value})}
                                                    sx={{fontSize: "12px"}}
                                                    renderValue={v => v ? `{{${v}}}` : "בחר שדה משלב קודם…"}
                                                >
                                                    <MenuItem value=""><em>ללא</em></MenuItem>
                                                    {!hasUpstream && (
                                                        <MenuItem value="" disabled><em>אין שלבים קודמים לחבר מהם</em></MenuItem>
                                                    )}
                                                    {upstreamGroups.flatMap(g => [
                                                        <ListSubheader key={`h-${g.sourceId}`} sx={{fontSize: "11px", fontWeight: 700, color: colors.primary, lineHeight: "28px", bgcolor: colors.white}}>
                                                            {g.sourceLabel}
                                                        </ListSubheader>,
                                                        ...g.fields.map(f => (
                                                            <MenuItem key={f.path} value={f.path} sx={{pr: 3}}>
                                                                <Box>
                                                                    <Typography sx={{fontSize: "12px", fontFamily: "monospace"}}>{`{{${f.path}}}`}</Typography>
                                                                    <Typography sx={{fontSize: "10px", color: colors.textMuted}}>{f.label}</Typography>
                                                                </Box>
                                                            </MenuItem>
                                                        )),
                                                    ])}
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Box>
                                );
                            }

                            // Password: choose RANDOM (empty binding → server generates one) or STATIC
                            // (a fixed password the trainer types). Mirrors the trainer selector's shape.
                            if (t.passwordMode) {
                                const pwMode = m.value ? "STATIC" : "RANDOM";
                                return (
                                    <Box key={t.key} sx={{
                                        p: 1, border: borders.default, borderRadius: borders.radius.md, bgcolor: colors.bg,
                                    }}>
                                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75}}>
                                            <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textPrimary}}>
                                                {t.label}
                                            </Typography>
                                            <FormControl size="small">
                                                <Select
                                                    value={pwMode}
                                                    onChange={e => setMap(t.key, {type: "STATIC", value: e.target.value === "RANDOM" ? "" : m.value || ""})}
                                                    sx={{fontSize: "11px", height: 26}}
                                                >
                                                    <MenuItem value="RANDOM">סיסמה אקראית</MenuItem>
                                                    <MenuItem value="STATIC">סיסמה קבועה</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        {pwMode === "RANDOM" ? (
                                            <Typography sx={{fontSize: "11px", color: colors.textMuted}}>
                                                תיווצר סיסמה אקראית, והמתאמן יתבקש להחליף בכניסה הראשונה.
                                            </Typography>
                                        ) : (
                                            <TextField
                                                fullWidth size="small"
                                                placeholder="סיסמה קבועה"
                                                value={m.value}
                                                onChange={e => setMap(t.key, {type: "STATIC", value: e.target.value})}
                                                sx={{"& input": {fontSize: "12px", fontFamily: "monospace"}}}
                                            />
                                        )}
                                    </Box>
                                );
                            }

                            // A target with a `picker` (program / nutrition / form) is chosen by NAME from a
                            // dropdown of the company's items; the picked id is stored as a STATIC binding.
                            if (t.picker) {
                                return (
                                    <Box key={t.key} sx={{
                                        p: 1, border: borders.default, borderRadius: borders.radius.md, bgcolor: colors.bg,
                                    }}>
                                        <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textPrimary, mb: 0.5}}>
                                            {t.label}{t.required && <span style={{color: colors.error}}> *</span>}
                                        </Typography>
                                        <IdPicker
                                            picker={t.picker}
                                            companyId={companyId}
                                            value={m.value}
                                            onChange={id => setMap(t.key, {type: "STATIC", value: id})}
                                        />
                                    </Box>
                                );
                            }

                            // The trainerId input offers a 3rd mode — "מאמן ברירת מחדל" (default) — which
                            // maps to an EMPTY binding: the server then assigns the company's default trainer.
                            // Specific = a static trainer id; From field = {{arbox.<field>}}.
                            const isTrainer = t.key === "trainerId";
                            const trainerMode = m.value ? (m.type === "FIELD" ? "FIELD" : "STATIC") : "DEFAULT";

                            return (
                                <Box key={t.key} sx={{
                                    p: 1, border: borders.default, borderRadius: borders.radius.md, bgcolor: colors.bg,
                                }}>
                                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75}}>
                                        <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textPrimary}}>
                                            {t.label}{t.required && <span style={{color: colors.error}}> *</span>}
                                            <Box component="span" sx={{fontSize: "10px", color: colors.textMuted, fontFamily: "monospace", mr: 0.5}}> {t.key}</Box>
                                        </Typography>
                                        {isTrainer ? (
                                            /* DEFAULT / STATIC / FIELD for the assigned trainer */
                                            <FormControl size="small">
                                                <Select
                                                    value={trainerMode}
                                                    onChange={e => {
                                                        const mode = e.target.value;
                                                        if (mode === "DEFAULT") setMap(t.key, {type: "STATIC", value: ""});
                                                        else setMap(t.key, {type: mode, value: ""});
                                                    }}
                                                    sx={{fontSize: "11px", height: 26}}
                                                >
                                                    <MenuItem value="DEFAULT">מאמן ברירת מחדל</MenuItem>
                                                    <MenuItem value="STATIC">מאמן מסוים</MenuItem>
                                                    <MenuItem value="FIELD">משדה של Arbox</MenuItem>
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            /* FIELD / STATIC toggle */
                                            <FormControl size="small">
                                                <Select
                                                    value={m.type === "HTML" ? "STATIC" : m.type}
                                                    onChange={e => setMap(t.key, {type: e.target.value, value: ""})}
                                                    sx={{fontSize: "11px", height: 26}}
                                                >
                                                    <MenuItem value="FIELD">משדה</MenuItem>
                                                    <MenuItem value="STATIC">ערך קבוע</MenuItem>
                                                </Select>
                                            </FormControl>
                                        )}
                                    </Box>

                                    {isTrainer && trainerMode === "DEFAULT" ? (
                                        <Typography sx={{fontSize: "11px", color: colors.textMuted}}>
                                            המתאמן ישויך למאמן ברירת המחדל של החברה.
                                        </Typography>
                                    ) : isTrainer && trainerMode === "STATIC" ? (
                                        <TextField
                                            fullWidth size="small" type="number"
                                            placeholder="מזהה מאמן (trainerId)"
                                            value={m.value}
                                            onChange={e => setMap(t.key, {type: "STATIC", value: e.target.value})}
                                            sx={{"& input": {fontSize: "12px"}}}
                                        />
                                    ) : m.type === "STATIC" ? (
                                        <TextField
                                            fullWidth size="small"
                                            placeholder='ערך קבוע, למשל me@gym.com או "שלום {{arbox.first_name}}"'
                                            value={m.value}
                                            onChange={e => setMap(t.key, {type: "STATIC", value: e.target.value})}
                                            sx={{"& input": {fontSize: "12px"}}}
                                        />
                                    ) : (
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={m.value || ""}
                                                displayEmpty
                                                onChange={e => setMap(t.key, {type: "FIELD", value: e.target.value})}
                                                sx={{fontSize: "12px"}}
                                                renderValue={v => v ? `{{${v}}}` : "בחר שדה משלב קודם…"}
                                            >
                                                <MenuItem value=""><em>ללא</em></MenuItem>
                                                {!hasUpstream && (
                                                    <MenuItem value="" disabled>
                                                        <em>אין שלבים קודמים לחבר מהם</em>
                                                    </MenuItem>
                                                )}
                                                {upstreamGroups.flatMap(g => [
                                                    <ListSubheader key={`h-${g.sourceId}`} sx={{fontSize: "11px", fontWeight: 700, color: colors.primary, lineHeight: "28px", bgcolor: colors.white}}>
                                                        {g.sourceLabel}
                                                    </ListSubheader>,
                                                    ...g.fields.map(f => (
                                                        <MenuItem key={f.path} value={f.path} sx={{pr: 3}}>
                                                            <Box>
                                                                <Typography sx={{fontSize: "12px", fontFamily: "monospace"}}>{`{{${f.path}}}`}</Typography>
                                                                <Typography sx={{fontSize: "10px", color: colors.textMuted}}>{f.label}</Typography>
                                                            </Box>
                                                        </MenuItem>
                                                    )),
                                                ])}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            )}

            {/* Options — behavior settings (booleans / selects) */}
            {action?.options?.length > 0 && (
                <Box>
                    <Typography sx={labelSx}>אפשרויות</Typography>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 0.5}}>
                        {action.options.map(o => (
                            <Box key={o.key} sx={{
                                p: 1, border: borders.default, borderRadius: borders.radius.md, bgcolor: colors.bg,
                            }}>
                                {o.type === "boolean" ? (
                                    <Box>
                                        <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                                            <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textPrimary}}>{o.label}</Typography>
                                            <Switch size="small" checked={Boolean(optionVal(o))}
                                                    onChange={e => setOption(o.key, e.target.checked)}/>
                                        </Box>
                                        {o.help && (
                                            <Typography sx={{fontSize: "10px", color: colors.textMuted, mt: 0.25}}>{o.help}</Typography>
                                        )}
                                    </Box>
                                ) : o.type === "text" ? (
                                    <Box>
                                        <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textPrimary, mb: 0.5}}>{o.label}</Typography>
                                        <TextField fullWidth size="small" value={optionVal(o) ?? ""}
                                                   onChange={e => setOption(o.key, e.target.value)}
                                                   sx={{"& input": {fontSize: "12px"}}}/>
                                        {o.help && <Typography sx={{fontSize: "10px", color: colors.textMuted, mt: 0.25}}>{o.help}</Typography>}
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography sx={{fontSize: "12px", fontWeight: 600, color: colors.textPrimary, mb: 0.5}}>{o.label}</Typography>
                                        <FormControl fullWidth size="small">
                                            <Select value={optionVal(o) ?? ""} onChange={e => setOption(o.key, e.target.value)} sx={{fontSize: "12px"}}>
                                                {(o.choices || []).map(c => (
                                                    <MenuItem key={c.value} value={c.value} sx={{fontSize: "12px"}}>{c.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {o.help && <Typography sx={{fontSize: "10px", color: colors.textMuted, mt: 0.25}}>{o.help}</Typography>}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Output preview — for data-transform nodes, show what field this
                produces so the user knows what to reference downstream. */}
            {isDataNode && action?.outputs?.length > 0 && (
                <Box>
                    <Typography sx={labelSx}>הפלט של השלב</Typography>
                    <Box sx={{p: 1, border: `1px dashed ${BRAND}`, borderRadius: borders.radius.md, bgcolor: `${BRAND}0D`}}>
                        {action.outputs.map(o => (
                            <Box key={o.key}>
                                <Typography sx={{fontSize: "12px", fontFamily: "monospace", color: BRAND}}>
                                    {`{{${(node.data?.brick?.kind || "step").toLowerCase()}_${node.id}.${o.key}}}`}
                                </Typography>
                                <Typography sx={{fontSize: "10px", color: colors.textMuted}}>{o.label} — זמין לשלבים הבאים</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </>
    );
}

export default function NodeConfigPanel({node, upstreamGroups = [], companyId, onChange, onClose, onDelete}) {
    // The Start node has no config; never open a panel for it.
    if (!node || node.type === "start") return null;
    return (
        <NodeConfigPanelInner
            key={node.id}
            node={node}
            upstreamGroups={upstreamGroups}
            companyId={companyId}
            onChange={onChange}
            onClose={onClose}
            onDelete={onDelete}
        />
    );
}

// Inner panel with a LOCAL DRAFT of the node's data. The sub-configs edit the draft (not the live
// graph); "שמור שלב" commits it via onChange, and closing/X discards. This makes editing an explicit
// action instead of the previous silent live-mutation (where pressing X seemed to save).
function NodeConfigPanelInner({node, upstreamGroups, companyId, onChange, onClose, onDelete}) {
    const isTrigger = node.type === "trigger";
    const isData = node.type === "data";

    const [draft, setDraft] = useState(node.data || {});
    // Dirty = the draft diverges from what's committed on the node.
    const dirty = JSON.stringify(draft) !== JSON.stringify(node.data || {});

    // The sub-configs read `node.data` and call `onChange(newData)` — feed them the DRAFT and route
    // their changes into setDraft, so nothing touches the real graph until Save.
    const draftNode = {...node, data: draft};

    const save = () => {
        onChange(draft);
        onClose();
    };
    const closeWithGuard = () => {
        if (dirty && !window.confirm("יש שינויים שלא נשמרו בשלב זה. לצאת בלי לשמור?")) return;
        onClose();
    };

    return (
        <RTL>
            <Box dir="rtl" sx={{
                width: 340, flexShrink: 0, height: "100%",
                bgcolor: colors.white, borderLeft: borders.default,
                display: "flex", flexDirection: "column",
            }}>
                <Box sx={{
                    px: 2, py: 1.5, borderBottom: borders.default,
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    bgcolor: colors.bg,
                }}>
                    <Typography sx={{fontSize: "15px", fontWeight: 700, color: colors.textPrimary}}>
                        {isTrigger ? "הגדרת טריגר" : isData ? "עיבוד נתונים" : "הגדרת פעולה"}
                    </Typography>
                    <IconButton size="small" onClick={closeWithGuard}><CloseIcon fontSize="small"/></IconButton>
                </Box>
                <Box sx={{p: 2, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", flex: 1}}>
                    {isTrigger && <TriggerConfig node={draftNode} companyId={companyId} onChange={setDraft}/>}
                    {/* Data-transform nodes reuse ActionConfig (inputs + options). */}
                    {!isTrigger && <ActionConfig node={draftNode} upstreamGroups={upstreamGroups} companyId={companyId} onChange={setDraft}/>}
                </Box>
                <Box sx={{p: 2, borderTop: borders.default, display: "flex", flexDirection: "column", gap: 1}}>
                    <Button
                        fullWidth variant="contained" size="small"
                        onClick={save}
                        disabled={!dirty}
                        sx={{bgcolor: BRAND, "&:hover": {bgcolor: BRAND}}}
                    >
                        {dirty ? "שמור שלב" : "נשמר"}
                    </Button>
                    {onDelete && (
                        <Button
                            fullWidth variant="outlined" color="error" size="small"
                            startIcon={<DeleteIcon fontSize="small"/>}
                            onClick={onDelete}
                        >
                            מחק שלב
                        </Button>
                    )}
                </Box>
            </Box>
        </RTL>
    );
}
